/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import * as MsDyn365 from '@msdyn365-commerce/core';
import { ProductSearchResult } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions, getProductPageUrlSync } from '@msdyn365-commerce-modules/retail-actions';
import {
    getPayloadObject, getTelemetryAttributes, getTelemetryObject,
    IModuleProps, INodeProps, isMobile, ITelemetryContent, VariantType
} from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

import {
    activeImageAdditionalContent,
    ActiveProductComponent,
    IActiveImageAdditionalContentViewProps,
    IArea,
    IAreaMap,
    IImageMapperProps,
    ImageMapper
} from '../active-image-utilities';
import { IActiveImageData } from './active-image.data';
import { IActiveImageProps } from './active-image.props.autogenerated';

/**
 * @type AreaEvent for active points
 */
type AreaEvent = Event | React.MouseEvent<HTMLAreaElement, MouseEvent> | React.FocusEvent<HTMLAreaElement>;

/**
 * @interface IActiveImageViewProps
 * @classdesc - The IActiveImageViewProps interface contract.
 */
export interface IActiveImageViewProps extends IActiveImageProps<{}>, IImageMapperProps {
    activePointContainer: IModuleProps;
    activePointDetailsContainer: INodeProps;
    imageMapper: React.ReactNode;
    overlay: React.ReactNode;
    productCard: React.ReactNode;
    title?: React.ReactNode;
    text?: React.ReactNode;
    links?: React.ReactNode;
    detailsContainer?: INodeProps;
    detailsContainerCta?: INodeProps;
    additionalContent?: IActiveImageAdditionalContentViewProps;
    productCardleftPosition: number;
    productCardtopPosition: number;
    handleAdditionalText?(event: MsDyn365.ContentEditableEvent): void;
    handleAdditionalParagraph?(event: MsDyn365.ContentEditableEvent): void;
    handleAdditionalLinkText?(event: MsDyn365.ContentEditableEvent): void;
}

/**
 * @interface IActiveImageLinksData
 * @classdesc - The IActiveImageLinksData interface contract.
 */
export interface IActiveImageLinksData {
    linkText?: string;
    linkUrl: MsDyn365.ILinkData;
    ariaLabel?: string;
    willOpenInNewTab?: boolean;
}

/**
 * @interface IActiveImageState
 * @classdesc - The IActiveImageState interface contract.
 */
interface IActiveImageState {
    clientWidth: number;
    area: IArea | null;
    viewport: string;
    overlayClass: string;
}

/**
 * @enum ActiveImageConstants
 * @description - The active image constants enum.
 */
export enum ActiveImageConstants {
    timeoutInterval = 200,
    enterKey = 'Enter',
    escKey = 'Escape'
}

/**
 *
 * ActiveImage class.
 * @extends {React.PureComponent<IActiveImageProps<{}> & IImageMapperProps, IActiveImageState>}
 */
export class ActiveImage extends React.PureComponent<IActiveImageProps<IActiveImageData> & IImageMapperProps, IActiveImageState> {
    private readonly _telemetryContent: ITelemetryContent;

    private _isDesktopViewPort: boolean;

    private readonly _defaultProductCardWidth: number = 315;

    private readonly _defaultProdutCardHight: number = 490;

    private readonly _activePoints: IArea[] = [];

    private readonly _activeImageRefs: React.RefObject<HTMLDivElement>;

    private readonly _activeImageClass: string;

    private readonly _productCardDivRef: React.RefObject<HTMLDivElement>;

    public constructor(props: IActiveImageProps<IActiveImageData> & IImageMapperProps) {
        super(props);
        this._telemetryContent = getTelemetryObject(this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry);
        const viewport = isMobile({ variant: VariantType.Browser, context: this.props.context.request });
        this._activeImageClass = `msc-active-image-${this.getActiveImageId()}`;
        this.state = {
            clientWidth: 0,
            area: null,
            viewport,
            overlayClass: ''
        };
        this._isDesktopViewPort = viewport === 'lg' || viewport === 'xl';
        this._activeImageRefs = React.createRef();
        this._productCardDivRef = React.createRef();
    }

    public componentDidMount(): void {
        if (MsDyn365.msdyn365Commerce.isBrowser) {
            window.addEventListener('resize', this.updateClientWidth);
            document.addEventListener('keydown', this.keyDown, false);
            document.addEventListener('mousedown', this._handleOutsideClick);
        }
        setTimeout(() => {
            this.updateClientWidth();
        }, ActiveImageConstants.timeoutInterval);

        if (MsDyn365.msdyn365Commerce.isBrowser && this._activeImageRefs.current) {
            const images: HTMLCollectionOf<Element> =
                this._activeImageRefs.current.getElementsByClassName('msc-thumbnail_image msc-active-image-img');
            if (ArrayExtensions.hasElements(Array.from(images))) {
                images[0].removeAttribute('usemap');
            }
        }
    }

    /**
     * AddActivePoints method - To handle add Active Points.
     * @param area - IArea.
     * @param product - ProductSearchResult.
     * @param coords - Coords.
     * @param productUrl - String.
     */
    public addActivePoints = (area: IArea | undefined, product: ProductSearchResult, coords: number[], productUrl: string): void => {
        if (!area) {
            this._activePoints.push({
                areaId: product.RecordId.toString(),
                coords,
                primaryImageUrl: product.PrimaryImageUrl ?? '',
                productUrl,
                productSearchResult: product
            });
        }
    };

    /**
     * ComponentWillUnmount method.
     */
    public componentWillUnmount(): void {
        if (MsDyn365.msdyn365Commerce.isBrowser) {
            window.removeEventListener('resize', this.updateClientWidth);
            document.removeEventListener('keydown', (this.keyDown as unknown) as EventListener, false);
            document.removeEventListener('mousedown', this._handleOutsideClick, false);
        }
    }

    /**
     * UpdateClientWidth method to update client width in state.
     */
    public updateClientWidth = (): void => {
        const viewport = isMobile({ variant: VariantType.Browser, context: this.props.context.request });
        this.setState({ clientWidth: this.getActiveImageWidth(), viewport });
        this._isDesktopViewPort = viewport === 'lg' || viewport === 'xl';
    };

    /**
     * Render method.
     * @returns - Returns JSX.Element | null.
     */
    public render(): JSX.Element | null {
        const viewProps: IActiveImageViewProps = {
            ...(this.props as IImageMapperProps & IActiveImageProps<{}>),
            activePointContainer: {
                moduleProps: this.props,
                className: classnames('msc-active-image', this._activeImageClass, this.props.config.className)
            },
            activePointDetailsContainer: {
                tag: 'div',
                className: classnames('msc-active-image-wrapper', this._activeImageClass, this.props.config.className),
                ref: this._activeImageRefs
            },
            imgWidth: this.props.config.canvasWidth,
            imageMapper: this.renderImageMapper(),
            overlay: this.renderOverlay(),
            productCard: this.renderProductCard(),
            productCardleftPosition: this.getProductCardPositionX(),
            productCardtopPosition: this.getProductCardPositionY(),
            title: this.renderActiveImageDetailsHeading(),
            text: this.renderActiveImageDetailsParagraph(),
            links: this.renderActiveImageDetailsLinks(),
            detailsContainer: { className: 'msc-active-image__details' },
            detailsContainerCta: { className: 'msc-active-image__details__cta' },
            additionalContent: activeImageAdditionalContent({
                requestContext: this.props.context.request,
                additionalContent: this.props.config.additionalContent,
                handleAdditionalTextChange: this.handleAdditionalTextChange,
                handleAdditionalParagraphChange: this.handleAdditionalParagraphChange,
                handleAdditionalLinkTextChange: this.handleAdditionalLinkTextChange
            }),
            handleAdditionalText: this.handleAdditionalTextChange,
            handleAdditionalParagraph: this.handleAdditionalParagraphChange,
            handleAdditionalLinkText: this.handleAdditionalTextChange
        };

        return this.props.renderView(viewProps);
    }

    /**
     * RenderImageMapper method - To render image mapper.
     * @returns - Returns React.ReactNode | null.
     */
    public renderImageMapper = (): React.ReactNode | null => {
        const { config, context } = this.props;
        const defaultPosition: number = 0;
        const defaultLength: number = 0;
        const productResults = this.props.data.products.result;
        if (config.activePoints && productResults) {
            for (const product of productResults) {
                for (const configProduct of config.activePoints) {
                    if (product.ItemId === configProduct.itemId) {
                        const productUrl = getProductPageUrlSync(product.Name ?? '', product.RecordId, context.actionContext, undefined);
                        const coords = [
                            configProduct.xPosition ?? defaultPosition,
                            configProduct.yPosition ?? defaultPosition,
                            configProduct.radius ?? defaultPosition
                        ];
                        const area = this._activePoints.find((activePoint) => JSON.stringify(activePoint.coords) === JSON.stringify(coords));
                        this.addActivePoints(area, product, coords, productUrl);
                    }
                }
            }
        }
        const mappedArea: IAreaMap = {
            name: `map-${this._activeImageClass}`,
            areas: this._activePoints.length > defaultLength ? this._activePoints : []
        };

        // If image is null, we want to render the placeholder SVG - by passing image data with empty src
        return (
            <ImageMapper
                viewPort={this.state.viewport}
                requestContext={this.props.context.actionContext.requestContext}
                width={this.state.clientWidth}
                imgWidth={this.props.config.canvasWidth}
                onClick={this.enterArea}
                onFocus={this.enterArea}
                onMouseEnter={this.enterArea}
                onMouseLeave={this.leaveArea}
                imageData={this.props.config.activeImage ?? {}}
                areaMap={mappedArea}
                activeImageRefs={this._activeImageRefs}
                imageMapperResources={{
                    altText: this.props.resources.altTextForSeeMoreButtonWithProductName,
                    buttonText: this.props.resources.seeMoreButtonText
                }}
            />
        );
    };

    /**
     * EnterArea method - To handle mouse enter event.
     * @param area - Active point area.
     * @param index - Active point index.
     * @param event - Active point AreaEvent.
     */
    public enterArea = (area: IArea, index: number, event: AreaEvent): void => {
        this.setState({ area, overlayClass: 'msc-active-image-overlay' });
        if (this.props.onMouseEnter) {
            this.props.onMouseEnter(area, index, event);
        }
        if (event.type.toLocaleLowerCase() === 'click' && this._isDesktopViewPort) {
            window.open(area.productUrl, '_self');
        }
    };

    /**
     * LeaveArea method - To handle mouse leave event.
     */
    public leaveArea = (): void => {
        if (this._isDesktopViewPort) {
            this.resetState();
        }
    };

    /**
     * ResetState method - To reset the state for area and overlayclass.
     */
    public resetState = (): void => {
        this.setState({ area: null, overlayClass: '' });
    };

    /**
     * RenderProductComponent method - To render the product card with product information.
     * @returns - Returns React.ReactNode.
     */
    public renderProductComponent = (): React.ReactNode => {
        if (this.state.area?.productSearchResult) {
            const baseImageUrl: string = this.props.context.request.apiSettings.baseImageUrl;
            const imageUrl: string = this.state.area.primaryImageUrl ?? '';
            const primaryImageUrl: string = `${baseImageUrl}${imageUrl}`;
            const product: ProductSearchResult = this.state.area.productSearchResult;
            product.PrimaryImageUrl = primaryImageUrl;

            return (
                <ActiveProductComponent
                    context={this.props.context}
                    imageSettings={this.props.config.productImageSettings}
                    id={product.ItemId ?? ''}
                    typeName={this.props.typeName || ''}
                    data={{ product }}
                />
            );
        }
        return React.Fragment;
    };

    /**
     * RenderProductCard method - To render product cards.
     * @returns - Returns React.ReactNode.
     */
    public renderProductCard = (): React.ReactNode => {
        const { area } = this.state;

        return (
            <>
                {area?.center && (
                    <div className='msc-active-image-product-card' ref={this._productCardDivRef}>
                        <div className='msc-active-image-product-card-wrapper' key={area.areaId}>
                            {!this._isDesktopViewPort && <button onClick={this.resetState} className='close-button' />}
                            {area.productSearchResult && this.renderProductComponent()}
                            {!this._isDesktopViewPort && <a href={area.productUrl} className='see-more-button'>
                                {this.props.resources.seeMoreButtonText}
                            </a>}
                        </div>
                    </div>
                )}
            </>
        );
    };

    /**
     * RenderOverlay method - To render overlay for non desktop viewports.
     * @returns - Returns React.ReactNode.
     */
    public renderOverlay = (): React.ReactNode => {
        return (
            <>
                {!this._isDesktopViewPort && <div className={this.state.overlayClass} />}
            </>
        );
    };

    /**
     * GetProductCardPositionX method - Provides product card x position to the left or right of active point based on available space.
     * For xs, sm and md viewports it provides x position for the center alignment.
     * @returns - Returns number.
     */
    public getProductCardPositionX = (): number => {
        const defaultImageSettings = this.getDefaultImageSettings();
        const productImageSettingsWidth =
            this.props.config.productImageSettings?.viewports ? this.props.config.productImageSettings.viewports.lg?.w : defaultImageSettings.viewports.lg?.w;
        const defaultPositionX: number = 0;
        const halfDivisor: number = 2;
        const productCardWidth: number =
            productImageSettingsWidth ? productImageSettingsWidth : this._defaultProductCardWidth;
        const { area, clientWidth, viewport } = this.state;
        const pointPositionX = area?.center?.[0] ?? clientWidth / halfDivisor;
        const leftAllignPosition = pointPositionX - productCardWidth - (productCardWidth / halfDivisor);
        const rightAllignPosition = pointPositionX + (productCardWidth / halfDivisor);
        let position: number = (clientWidth - productCardWidth) / halfDivisor;
        if (viewport !== 'xs' && viewport !== 'sm' && viewport !== 'md') {
            if (leftAllignPosition < defaultPositionX) {
                position = rightAllignPosition;
            } else {
                position = leftAllignPosition;
            }
        }
        return position;
    };

    /**
     * GetProductCardPositionY method - Provides product card y position in center with respect to active point.
     * For xs, sm and md viewports it provides y position for the center alignment.
     * @returns - Returns number.
     */
    public getProductCardPositionY = (): number => {
        const defaultImageSettings = this.getDefaultImageSettings();
        const productImageSettingsHeight =
            this.props.config.productImageSettings?.viewports ? this.props.config.productImageSettings.viewports.lg?.h : defaultImageSettings.viewports.lg?.h;
        const halfDivisor: number = 2;
        const { area, viewport } = this.state;
        const produtCardHight: number =
            productImageSettingsHeight ? productImageSettingsHeight : this._defaultProdutCardHight;
        const pointPositionY = (this.getActiveImageHeight() - produtCardHight) / halfDivisor;
        let position: number = pointPositionY;
        if (viewport !== 'xs' && viewport !== 'sm' && viewport !== 'md') {
            position = (area?.center?.[1] ?? this.getActiveImageHeight() / halfDivisor) - (produtCardHight / halfDivisor);
        }
        return position;
    };

    /**
     * GetDefaultImageSettings method.
     * @returns MsDyn365.IImageSettings.
     */
    public getDefaultImageSettings = (): MsDyn365.IImageSettings => {
        const defaultImageSettings: MsDyn365.IImageSettings = {
            viewports: {
                xs: { q: 'w=275&h=275&m=6', w: 275, h: 275 },
                lg: { q: 'w=275&h=275&m=6', w: 275, h: 275 },
                xl: { q: 'w=275&h=275&m=6', w: 275, h: 275 }
            },
            lazyload: true
        };

        return defaultImageSettings;
    };

    /**
     * KeyDown method - To handle keydown event.
     * @param event - KeyboardEvent.
     */
    public keyDown = (event: KeyboardEvent): void => {
        if (event.key === ActiveImageConstants.enterKey) {
            const target: HTMLElement = event.target as HTMLElement;
            if (target.nodeName === 'AREA') {
                const area: HTMLAreaElement = target as HTMLAreaElement;
                if (area.href) {
                    window.open(area.href, '_self');
                }
            }
        }
        if (event.key === ActiveImageConstants.escKey) {
            this.resetState();
        }
    };

    /**
     * GetActiveImageWidth method - To get active image width.
     * @returns Returns number.
     */
    public getActiveImageWidth = (): number => {
        const defaultWidth: number = 0;
        if (MsDyn365.msdyn365Commerce.isBrowser) {
            const activeImageReferenceValue = this._activeImageRefs;
            if (activeImageReferenceValue.current !== null) {
                return activeImageReferenceValue.current.clientWidth;
            }
        }
        return defaultWidth;
    };

    /**
     * GetActiveImageHeight method - To get active image height.
     * @returns Returns number.
     */
    public getActiveImageHeight = (): number => {
        const defaultHeight: number = 0;
        if (MsDyn365.msdyn365Commerce.isBrowser) {
            const activeImageReferenceValue = this._activeImageRefs;
            if (activeImageReferenceValue.current !== null) {
                return activeImageReferenceValue.current.clientHeight;
            }
        }
        return defaultHeight;
    };

    /**
     * HandleLinkTextChange method - To handle link text change event.
     * @param linkIndex - The link index.
     * @param event - The dialog that is allowed to remain open.
     */
    public handleLinkTextChange(linkIndex: number, event: MsDyn365.ContentEditableEvent): void {
        if (this.props.config.links && this.props.config.links[Number(linkIndex)]) {
            this.props.config.links[Number(linkIndex)].linkText = event.target.value;
        }
    }

    /**
     * HandleTextChange method - To handle text change event.
     * @param event - The dialog that is allowed to remain open.
     */
    public handleTextChange = (event: MsDyn365.ContentEditableEvent): void => {
        this.props.config.heading!.text = event.target.value;
    };

    /**
     * HandleParagraphChange method - To handle paragraph change event.
     * @param event - The dialog that is allowed to remain open.
     */
    public handleParagraphChange = (event: MsDyn365.ContentEditableEvent): void => {
        this.props.config.paragraph = event.target.value;
    };

    /**
     * HandleAdditionalTextChange method - To handle additional text change event.
     * @param event - The dialog that is allowed to remain open.
     */
    public handleAdditionalTextChange(event: MsDyn365.ContentEditableEvent): void {
        if (this.props.config.additionalContent) {
            this.props.config.additionalContent.heading = event.target.value;
        }
    }

    /**
     * HandleAdditionalLinkTextChange method - To handle additional link text change event.
     * @param linkIndex - The link index.
     * @param event - The dialog that is allowed to remain open.
     */
    public handleAdditionalLinkTextChange(linkIndex: number, event: MsDyn365.ContentEditableEvent): void {
        const additionalContent = this.props.config.additionalContent ?? {};
        if (additionalContent.links) {
            additionalContent.links[Number(linkIndex)].linkText = event.target.value;
        }
    }

    /**
     * HandleAdditionalParagraphChange method - To handle additional paragraph change event.
     * @param event - The dialog that is allowed to remain open.
     */
    public handleAdditionalParagraphChange(event: MsDyn365.ContentEditableEvent): void {
        if (this.props.config.additionalContent) {
            this.props.config.additionalContent.subtext = event.target.value;
        }
    }

    /**
     * RenderActiveImageDetailsLinks method - To render active image details links.
     * @returns - React node.
     */
    public renderActiveImageDetailsLinks = (): React.ReactNode[] | undefined => {
        const { links } = this.props.config;
        const payLoad = getPayloadObject('click', this._telemetryContent, '');

        return (
            links?.map((linkData: IActiveImageLinksData, index: number) => {
                payLoad.contentAction.etext = linkData.linkText;
                const attributes = getTelemetryAttributes(this._telemetryContent, payLoad);
                const linkId = index;
                const editableLink: MsDyn365.ILinksData = {
                    ariaLabel: linkData.ariaLabel,
                    className: 'msc-active-image__details__cta__link',
                    linkText: linkData.linkText,
                    linkUrl: linkData.linkUrl.destinationUrl,
                    openInNewTab: linkData.willOpenInNewTab,
                    role: 'button',
                    additionalProperties: attributes
                };

                return (
                    <MsDyn365.Link
                        key={linkId}
                        link={editableLink}
                        editProps={{
                            /**
                             * Handle additional paragraph change.
                             * @param event - The keyboard event.
                             */
                            onTextChange: (event) => {
                                this.handleLinkTextChange(index, event);
                            }, requestContext: this.props.context.request
                        }}
                    />
                );
            })
        );
    };

    /**
     * RenderActiveImageDetailsHeading method - To render active image details heading.
     * @returns - React.ReactNode | undefined.
     */
    public renderActiveImageDetailsHeading = (): React.ReactNode | undefined => {
        const { heading } = this.props.config;

        return (
            heading && (
                <MsDyn365.Text
                    className='msc-active-image__details__title'
                    tag={heading.tag ?? 'h1'}
                    text={heading.text}
                    editProps={{ onEdit: this.handleTextChange, requestContext: this.props.context.request }}
                />
            )
        );
    };

    /**
     * RenderActiveImageDetailsParagraph method - To render active image details paragraph.
     * @returns - React.ReactNode | undefined.
     */
    public renderActiveImageDetailsParagraph = (): React.ReactNode | undefined => {
        const { paragraph } = this.props.config;

        return (
            paragraph && (
                <MsDyn365.RichTextComponent
                    text={paragraph}
                    className='msc-active-image__details__text'
                    editProps={{ onEdit: this.handleParagraphChange, requestContext: this.props.context.request }}
                />
            )
        );
    };

    /**
     * GetActiveImageId - To get active image id from src.
     * @returns - String.
     */
    public getActiveImageId = (): string => {
        const { activeImage } = this.props.config;
        let activeImageId: string = '';
        if (activeImage?.src) {
            const activeImageSource = activeImage.src.split('/');
            const decrementOne: number = 1;
            const activeImageIdPart = (activeImageSource[activeImageSource.length - decrementOne]).split('?');
            activeImageId = activeImageIdPart[0] ?? '';
        }
        return activeImageId;
    };

    /**
     * Handle outside click method - To handle outside click of the active image product.
     * @param event - Mouse event.
     */
    private readonly _handleOutsideClick = (event: MouseEvent) => {
        if (this._productCardDivRef.current?.contains(event.target as Node)) {
            return;
        }
        this.resetState();
    };
}
export default ActiveImage;
